// DEPLOY: /var/www/woody.wtf/frontend/src/App.js
// Top-level router. Defines all routes, including /chart (chart.ninja's own chart engine, pages/Chart2.js; /chart2 kept as an alias) and /chart-old (old Highcharts chart, backup until the new one is signed off), admin-only /ai (AI Plane), /admin/picks (AI Picks) and the /trading section (Portfolio, Trade, Options, Orders, Currency).
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import api from './api';
import { AuthProvider, useAuth } from './context/AuthContext';
import { loadAccountAppearance } from './appearance';
import Login                from './pages/Login';
import Register             from './pages/Register';
import Confirm              from './pages/Confirm';
import ResetPassword        from './pages/ResetPassword';
import Dashboard            from './pages/Dashboard';
import Settings             from './pages/Settings';
import ChartPage            from './pages/Chart';
import Chart2               from './pages/Chart2';
import MortgageCalculator   from './pages/MortgageCalculator';
import InvestmentCalculator from './pages/InvestmentCalculator';
import MarketScanner        from './pages/MarketScanner';
import Workspace            from './pages/Workspace';
import Compare              from './pages/Compare';
import IndexPerformance     from './pages/IndexPerformance';
import Breadth              from './pages/Breadth';
import Admin                from './pages/Admin';
import ScansWorkspace       from './pages/ScansWorkspace';
import EtfWorkspace         from './pages/EtfWorkspace';
import EtfScreener          from './pages/EtfScreener';
import AdminEtf             from './pages/AdminEtf';
import AdminLeveraged       from './pages/AdminLeveraged';
import AdminDbMap           from './pages/AdminDbMap';
import AdminDirMap          from './pages/AdminDirMap';
import StrategyBuilder      from './pages/StrategyBuilder';
import AiPlane              from './pages/AiPlane';
import Analysts             from './pages/Analysts';
import AnalystProfile       from './pages/AnalystProfile';
import FdaApprovals         from './pages/FdaApprovals';
import CompanyPage          from './pages/Company';
import AdminPrompts         from './pages/AdminPrompts';
import AdminDiscord         from './pages/AdminDiscord';
import AdminLogs            from './pages/AdminLogs';
import AdminJobLog          from './pages/AdminJobLog';
import AdminSecurity        from './pages/AdminSecurity';
import ApproveIp            from './pages/ApproveIp';
import AdminAudit           from './pages/AdminAudit';
import AdminStats           from './pages/AdminStats';
import AdminBacktest        from './pages/AdminBacktest';
import AdminFirstN          from './pages/AdminFirstN';
import SmcLab               from './pages/SmcLab';
import AiPicks              from './pages/AiPicks';
import AnnouncementsPage    from './pages/Announcements';
import Trading              from './pages/Trading';
import TradingTrade         from './pages/TradingTrade';
import TradingOptions       from './pages/TradingOptions';
import TradingOrders        from './pages/TradingOrders';
import TradingFx            from './pages/TradingFx';
import TradingMovers        from './pages/TradingMovers';
import MoverAudit           from './pages/MoverAudit';
import Help                 from './pages/Help';
import Events               from './pages/Events';

// Isolated mobile prototype (Stage 0 PoC) - lazy so it stays out of the main bundle.
const MobilePoc = React.lazy(() => import('./mobile/MobilePoc'));
const MobileChart = React.lazy(() => import('./mobile/MobileChart'));

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location          = useLocation();
  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', color:'var(--text-muted)' }}>Loading...</div>;
  return user ? children : <Navigate to="/login" state={{ from: location.pathname }} replace />;
}

// Unknown in-app route: reported to the security log (same endpoint as Apache's probe trap),
// then the old behaviour - back to the login page.
function NotFound() {
  const location = useLocation();
  useEffect(() => { api.post('/security/probe', { path: location.pathname }).catch(() => {}); }, [location.pathname]);
  return <Navigate to="/login" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login"                  element={<Login />} />
      <Route path="/register"               element={<Register />} />
      <Route path="/confirm"                element={<Confirm />} />
      <Route path="/reset-password"         element={<ResetPassword />} />
      <Route path="/approve-ip"             element={<ApproveIp />} />
      <Route path="/dashboard"              element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/settings"               element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/chart"                  element={<ProtectedRoute><Chart2 /></ProtectedRoute>} />
      <Route path="/chart2"                 element={<ProtectedRoute><Chart2 /></ProtectedRoute>} />
      <Route path="/chart-old"              element={<ProtectedRoute><ChartPage /></ProtectedRoute>} />
      <Route path="/company"                element={<ProtectedRoute><CompanyPage /></ProtectedRoute>} />
      <Route path="/announcements"          element={<ProtectedRoute><AnnouncementsPage /></ProtectedRoute>} />
      <Route path="/mobile-poc/:ticker"     element={<React.Suspense fallback={null}><MobilePoc /></React.Suspense>} />
      <Route path="/mobile-chart/:ticker"   element={<React.Suspense fallback={null}><MobileChart /></React.Suspense>} />
      <Route path="/analysts"               element={<ProtectedRoute><Analysts /></ProtectedRoute>} />
      <Route path="/analysts/analyst/:name" element={<ProtectedRoute><AnalystProfile /></ProtectedRoute>} />
      <Route path="/fda"                    element={<ProtectedRoute><FdaApprovals /></ProtectedRoute>} />
      <Route path="/mortgage-calculator"    element={<ProtectedRoute><MortgageCalculator /></ProtectedRoute>} />
      <Route path="/investment-calculator"  element={<ProtectedRoute><InvestmentCalculator /></ProtectedRoute>} />
      <Route path="/scanner"                element={<ProtectedRoute><MarketScanner /></ProtectedRoute>} />
      <Route path="/workspace"              element={<ProtectedRoute><Workspace /></ProtectedRoute>} />
      <Route path="/compare"                element={<ProtectedRoute><Compare /></ProtectedRoute>} />
      <Route path="/indexes"                element={<ProtectedRoute><IndexPerformance /></ProtectedRoute>} />
      <Route path="/events"                 element={<ProtectedRoute><Events /></ProtectedRoute>} />
      <Route path="/breadth"                element={<ProtectedRoute><Breadth /></ProtectedRoute>} />
      <Route path="/admin"                  element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/scans"                  element={<ProtectedRoute><ScansWorkspace /></ProtectedRoute>} />
      <Route path="/etf/workspace"          element={<ProtectedRoute><EtfWorkspace /></ProtectedRoute>} />
      <Route path="/etf"                    element={<EtfScreener />} />
      <Route path="/admin/etf"              element={<AdminEtf />} />
      <Route path="/admin/leveraged"        element={<AdminLeveraged />} />
      <Route path="/admin/db-map"           element={<ProtectedRoute><AdminDbMap /></ProtectedRoute>} />
      <Route path="/admin/dir-map"          element={<ProtectedRoute><AdminDirMap /></ProtectedRoute>} />
      <Route path="/admin/prompts"          element={<ProtectedRoute><AdminPrompts /></ProtectedRoute>} />
      <Route path="/admin/discord"          element={<ProtectedRoute><AdminDiscord /></ProtectedRoute>} />
      <Route path="/admin/logs"             element={<ProtectedRoute><AdminLogs /></ProtectedRoute>} />
      <Route path="/admin/joblog"           element={<ProtectedRoute><AdminJobLog /></ProtectedRoute>} />
      <Route path="/admin/security"         element={<ProtectedRoute><AdminSecurity /></ProtectedRoute>} />
      <Route path="/admin/audit"            element={<ProtectedRoute><AdminAudit /></ProtectedRoute>} />
      <Route path="/admin/stats"            element={<ProtectedRoute><AdminStats /></ProtectedRoute>} />
      <Route path="/admin/backtest"         element={<ProtectedRoute><AdminBacktest /></ProtectedRoute>} />
      <Route path="/admin/firstn"           element={<ProtectedRoute><AdminFirstN /></ProtectedRoute>} />
      <Route path="/admin/smc"              element={<ProtectedRoute><SmcLab /></ProtectedRoute>} />
      <Route path="/admin/picks"            element={<ProtectedRoute><AiPicks /></ProtectedRoute>} />
      <Route path="/strategy"               element={<ProtectedRoute><StrategyBuilder /></ProtectedRoute>} />
      <Route path="/ai"                     element={<ProtectedRoute><AiPlane /></ProtectedRoute>} />
      <Route path="/trading"                element={<ProtectedRoute><Trading /></ProtectedRoute>} />
      <Route path="/trading/trade"          element={<ProtectedRoute><TradingTrade /></ProtectedRoute>} />
      <Route path="/trading/options"        element={<ProtectedRoute><TradingOptions /></ProtectedRoute>} />
      <Route path="/trading/orders"         element={<ProtectedRoute><TradingOrders /></ProtectedRoute>} />
      <Route path="/trading/fx"             element={<ProtectedRoute><TradingFx /></ProtectedRoute>} />
      <Route path="/trading/movers"         element={<ProtectedRoute><TradingMovers /></ProtectedRoute>} />
      <Route path="/movers/audit"           element={<ProtectedRoute><MoverAudit /></ProtectedRoute>} />
      <Route path="/help"                   element={<ProtectedRoute><Help /></ProtectedRoute>} />
      <Route path="*"                       element={<NotFound />} />
    </Routes>
  );
}

// Loads the signed-in user's saved theme and candle style (Settings -> Appearance).
function AppearanceSync() {
  const { user } = useAuth();
  useEffect(() => { if (user) loadAccountAppearance(); }, [user]);
  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <AppearanceSync />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
